import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
  ColumnDef,
  ColumnFiltersState,
  HeaderGroup,
  Row,
  RowModel,
  Table,
  VisibilityState,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { URLS } from 'components/shared/App/constants';
import { selectors } from 'store';

import { FormattedCredit, formatCreditData } from './helpers';

export interface CreditSearchTableHook {
  showTable: boolean;
  table: Table<FormattedCredit>;
  header: HeaderGroup<FormattedCredit>[];
  rows: RowModel<FormattedCredit>;
  onClick: (row: any) => void;
}

export default function useCreditSearchTable(): CreditSearchTableHook {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    id: false,
  });
  const { push } = useHistory();

  const searchData = useSelector(selectors.infoSearch.getEntity);

  const onClick = useCallback(
    (row: Row<any>) => {
      const { original } = row;
      const id = original?.id;
      if (!id) return;
      push(URLS.payment.replace(':id', id));
    },
    [push],
  );

  const data = useMemo(
    () => formatCreditData(searchData?.hits?.credit),
    [searchData],
  );

  const columns = useMemo<ColumnDef<FormattedCredit, any>[]>(
    () => [
      {
        accessorKey: 'id',
        header: () => <p>Credit ID</p>,
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'rate',
        header: () => <p>Credit Rate</p>,
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'provider',
        header: () => <p>Credit Provider</p>,
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'xid',
        header: () => <p>Credit XID</p>,
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'customerName',
        header: () => <p>Customer Name</p>,
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'customerEmail',
        header: () => <p>Customer Email</p>,
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'customerPhone',
        header: () => <p>Customer Phone</p>,
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'customerAddress',
        header: () => <p>Customer Address</p>,
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'createdAt',
        header: () => <p>Credit Created</p>,
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'updatedAt',
        header: () => <p>Credit Created</p>,
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
    ],
    [],
  );

  const table = useReactTable<FormattedCredit>({
    data,
    columns,
    state: {
      columnFilters,
      globalFilter,
      columnVisibility,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
  });

  return {
    showTable: data.length > 0,
    table,
    header: table.getHeaderGroups(),
    rows: table.getRowModel(),
    onClick,
  };
}
